<template>
  <div
    class="login-container"
    :class="{ 'is-bg-loaded': backgroundLoaded }"
    :style="backgroundStyle"
  >
    <div class="login-box">
      <div class="login-header">
        <h1 class="platform-name">于垣视频生成平台</h1>
        <h2 class="login-title">欢迎登录</h2>
        <p class="login-subtitle">请输入您的账号信息</p>
      </div>
      <el-form
        ref="loginFormRef"
        :model="loginForm"
        :rules="loginRules"
        label-width="0"
        class="login-form"
      >
        <el-form-item label="" prop="username" class="form-item-custom">
          <el-input
            v-model="loginForm.username"
            :placeholder="isMobile ? '用户名' : '请输入用户名'"
            size="large"
            clearable
            class="custom-input"
          >
            <template #prefix>
              <el-icon class="input-icon"><User /></el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item label="" prop="password" class="form-item-custom">
          <el-input
            v-model="loginForm.password"
            type="password"
            :placeholder="isMobile ? '密码' : '请输入密码'"
            size="large"
            show-password
            clearable
            class="custom-input"
            @keyup.enter="handleLogin"
          >
            <template #prefix>
              <el-icon class="input-icon"><Lock /></el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item class="form-item-button">
          <el-button
            type="primary"
            :loading="loading"
            @click="handleLogin"
            class="login-button"
            size="large"
          >
            {{ loading ? '登录中...' : '登录' }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { User, Lock } from '@element-plus/icons-vue'
import { login, setToken, setUserInfo, setPermissions } from '@/api/auth'
import loginBg from '@/assets/images/login_bg.webp'

export default {
  name: 'LoginPage',
  components: {
    User,
    Lock
  },
  setup() {
    const router = useRouter()
    const loginFormRef = ref(null)
    const loading = ref(false)
    const isMobile = ref(false)
    const backgroundLoaded = ref(false)
    const backgroundStyle = {
      '--login-bg-image': `url("${loginBg}")`
    }
    let backgroundPreloader = null
    
    const loginForm = reactive({
      username: '',
      password: ''
    })

    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768
    }

    const validateUsername = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入用户名'))
      } else {
        callback()
      }
    }

    const validatePassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入密码'))
      } else {
        callback()
      }
    }

    const loginRules = {
      username: [{ validator: validateUsername, trigger: 'blur' }],
      password: [{ validator: validatePassword, trigger: 'blur' }]
    }

    const handleLogin = () => {
      if (loading.value) return
      loginFormRef.value.validate((valid) => {
        if (valid) {
          loading.value = true
          
          // 调用登录接口
          login({
            username: loginForm.username,
            password: loginForm.password
          })
            .then((response) => {
              console.log('登录响应:', response)
              // 登录成功
              if (response.code === 200 && response.data) {
                console.log('登录数据:', response.data)
                // 保存 token
                if (!response.data.token) {
                  console.error('响应中没有 token:', response.data)
                  ElMessage.error('登录响应无效，请稍后重试')
                  return
                }
                if (response.data.token) {
                  setToken(response.data.token)
                  console.log('Token 保存成功')
                } else {
                  console.error('响应中没有 token:', response.data)
                }
                // 保存用户信息
                setUserInfo({
                  userId: response.data.userId,
                  username: response.data.username,
                  nickname: response.data.nickname,
                  avatar: response.data.avatar,
                  generateCount: response.data.generateCount || 0,
                  roles: response.data.roles || [],
                  menus: response.data.menus || []
                })

                // 保存用户权限
                if (response.data.permissions) {
                  setPermissions(response.data.permissions)
                }
                
                ElMessage.success(response.message || '登录成功')

                // 跳转到登录前的页面，没有则跳转到视频列表
                const redirectPath = router.currentRoute.value.query.redirect || '/videos'
                console.log('登录成功，即将跳转到:', redirectPath)
                router.push(redirectPath)
              } else {
                console.error('登录失败，响应:', response)
                ElMessage.error(response.message || '登录失败')
              }
            })
            .catch((error) => {
              console.error('登录失败:', error)
              // 错误信息已在拦截器中处理
            })
            .finally(() => {
              loading.value = false
            })
        } else {
          ElMessage.error('请填写完整的登录信息')
          return false
        }
      })
    }

    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', checkMobile)

      backgroundPreloader = new Image()
      backgroundPreloader.onload = () => {
        backgroundLoaded.value = true
      }
      backgroundPreloader.src = loginBg
    })

    onUnmounted(() => {
      window.removeEventListener('resize', checkMobile)
      if (backgroundPreloader) {
        backgroundPreloader.onload = null
        backgroundPreloader = null
      }
    })

    return {
      loginFormRef,
      loginForm,
      loginRules,
      loading,
      isMobile,
      backgroundLoaded,
      backgroundStyle,
      handleLogin
    }
  }
}
</script>

<style scoped>
.login-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  padding: 20px;
  background:
    radial-gradient(circle at 22% 18%, rgba(59, 130, 246, 0.22), transparent 32%),
    radial-gradient(circle at 78% 68%, rgba(6, 182, 212, 0.18), transparent 34%),
    linear-gradient(135deg, #e8f1ff 0%, #f8fbff 46%, #eef8fb 100%);
  overflow: hidden;
  box-sizing: border-box;
}

.login-container::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: var(--login-bg-image) no-repeat center center / cover;
  opacity: 0;
  transition: opacity 0.45s ease;
}

.login-container.is-bg-loaded::before {
  opacity: 1;
}

.login-box {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  padding: 50px 40px;
  /* 白色卡片：轻微玻璃感 */
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(14px);
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 20px;
  box-shadow:
    0 24px 80px rgba(15, 23, 42, 0.12),
    0 2px 10px rgba(15, 23, 42, 0.06);
  animation: slideUp 0.5s ease-out;
  box-sizing: border-box;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-header {
  text-align: center;
  margin-bottom: 40px;
}

.platform-name {
  margin: 0 0 20px 0;
  color: #0f172a;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: 2px;
}

.login-title {
  margin: 0 0 10px 0;
  color: #0f172a;
  font-size: 32px;
  font-weight: 700;
  /* 白色系下的主标题渐变 */
  background: linear-gradient(135deg, #1d4ed8 0%, #3b82f6 55%, #06b6d4 110%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 1px;
}

.login-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  font-weight: 400;
}

.login-form {
  margin-top: 10px;
  width: 100%;
}

.form-item-custom {
  margin-bottom: 24px;
  width: 100%;
}

.form-item-custom :deep(.el-form-item__content) {
  margin-left: 0 !important;
  width: 100%;
}

.form-item-button {
  margin-top: 32px;
  margin-bottom: 0;
  width: 100%;
}

.form-item-button :deep(.el-form-item__content) {
  margin-left: 0 !important;
  width: 100%;
}

.custom-input {
  transition: all 0.3s ease;
  width: 100%;
}

.custom-input :deep(.el-input__wrapper) {
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
  transition: all 0.3s ease;
  padding: 12px 16px;
}

.custom-input :deep(.el-input__inner) {
  /* 输入区域透明，保持白底干净 */
  background-color: transparent !important;
  border: none;
  box-shadow: none;
  color: #0f172a;
}

.custom-input :deep(.el-input__wrapper:hover) {
  border-color: rgba(37, 99, 235, 0.35);
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.12);
}

.custom-input :deep(.el-input__wrapper.is-focus) {
  border-color: rgba(37, 99, 235, 0.55);
  box-shadow: 0 12px 28px rgba(37, 99, 235, 0.16);
}

/* 覆盖浏览器/密码管理器 autofill 造成的输入框偏色（常见为浅蓝/浅黄底） */
.custom-input :deep(.el-input__inner:-webkit-autofill),
.custom-input :deep(.el-input__inner:-webkit-autofill:hover),
.custom-input :deep(.el-input__inner:-webkit-autofill:focus),
.custom-input :deep(.el-input__inner:-webkit-autofill:active) {
  -webkit-text-fill-color: #0f172a;
  -webkit-box-shadow: 0 0 0 1000px #ffffff inset;
  box-shadow: 0 0 0 1000px #ffffff inset;
  transition: background-color 9999s ease-out 0s;
}

.input-icon {
  color: #2563eb;
  font-size: 18px;
}

.login-button {
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 45%, #3b82f6 100%);
  border: none;
  box-shadow: 0 10px 26px rgba(37, 99, 235, 0.28);
  transition: all 0.3s ease;
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 32px rgba(37, 99, 235, 0.34);
}

.login-button:active {
  transform: translateY(0);
}

:deep(.el-form-item__error) {
  padding-top: 4px;
  font-size: 12px;
}

/* 手机端适配 */
@media (max-width: 768px) {
  .login-container {
    padding: 15px;
    align-items: center;
  }

  .login-box {
    padding: 35px 25px;
    border-radius: 16px;
    max-width: 100%;
  }

  .platform-name {
    font-size: 22px;
    margin-bottom: 15px;
    letter-spacing: 1px;
  }

  .login-title {
    font-size: 26px;
    margin-bottom: 8px;
  }

  .login-subtitle {
    font-size: 13px;
  }

  .login-header {
    margin-bottom: 30px;
  }

  .form-item-custom {
    margin-bottom: 20px;
  }

  .form-item-button {
    margin-top: 28px;
  }

  .custom-input :deep(.el-input__wrapper) {
    padding: 10px 14px;
  }

  .login-button {
    height: 44px;
    font-size: 15px;
  }
}

/* 小屏手机适配 */
@media (max-width: 480px) {
  .login-container {
    padding: 10px;
    align-items: center;
  }

  .login-box {
    padding: 30px 20px;
  }

  .platform-name {
    font-size: 20px;
    margin-bottom: 12px;
    letter-spacing: 1px;
  }

  .login-title {
    font-size: 24px;
  }

  .login-subtitle {
    font-size: 12px;
  }

  .login-header {
    margin-bottom: 25px;
  }

  .form-item-custom {
    margin-bottom: 18px;
  }

  .form-item-button {
    margin-top: 24px;
  }
}

/* 横屏适配 */
@media (max-height: 600px) and (orientation: landscape) {
  .login-container {
    padding-top: 5vh;
    padding-bottom: 5vh;
  }

  .login-box {
    padding: 30px 35px;
  }

  .login-header {
    margin-bottom: 25px;
  }

  .form-item-custom {
    margin-bottom: 16px;
  }

  .form-item-button {
    margin-top: 20px;
  }
}
</style>
